import { Trans, useLingui } from '@lingui/react/macro';
import { Box, Typography } from '@mui/material';
import { useCallback, useEffect, useRef } from 'react';
import { InlineButton } from '@/components/button/inline_button';
import type { TokenDetail } from '@/utils/tokenNumberUtils';
import { DecimalInputField } from './decimal_input_field';

interface TokenInputFieldProps {
  token?: TokenDetail;
  value: string;
  onChange: (value: string) => void;
  onMaxClick: () => void;
  disabled?: boolean;
  maxAmount?: { value: bigint; decimals: number };
  errorMessage?: string;
  formattedMaxAmount: string;
  autoFocus?: boolean;
  inputId?: string;
  label?: string;
  /** When false, hides the label + available-amount header row above the input. Defaults to true. */
  showHeader?: boolean;
}

export function TokenInputField({
  token,
  value,
  onChange,
  onMaxClick,
  disabled = false,
  maxAmount,
  errorMessage,
  formattedMaxAmount,
  autoFocus = true,
  inputId = 'tokenAmount',
  label,
  showHeader = true,
}: TokenInputFieldProps) {
  const { t } = useLingui();
  const effectiveLabel = label ?? t({ comment: 'Token amount input label', message: 'Amount' });
  const tokenSymbol = token?.symbol || '';
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!autoFocus) return;
    inputRef.current?.focus({ preventScroll: true });
  }, [autoFocus]);

  const handleMaxClick = useCallback(() => {
    onMaxClick();
    // Refocus the input after clicking max so hitting 'Enter' submits the form
    setTimeout(() => {
      inputRef.current?.focus({ preventScroll: true });
    }, 0);
  }, [onMaxClick]);

  return (
    <>
      {showHeader && (
        <Box display="flex" justifyContent="space-between" mb={1}>
          {effectiveLabel && <Typography variant="caption">{effectiveLabel}</Typography>}
          <Typography variant="caption">
            <Trans>
              Available: <span className="numeric">{formattedMaxAmount}</span> {tokenSymbol}
            </Trans>
          </Typography>
        </Box>
      )}
      <DecimalInputField
        id={inputId}
        inputRef={inputRef}
        size="medium"
        maxPrecision={token?.display_precision}
        value={value}
        onChange={onChange}
        disabled={disabled}
        error={!!errorMessage}
        helperText={errorMessage || ' '}
        required
        endAdornment={
          <InlineButton onClick={handleMaxClick} disabled={!maxAmount || disabled} tabIndex={-1}>
            <Trans comment="Button that fills in the maximum available amount. Keep the Latin `MAX` — it is the conventional label on exchange amount inputs and the button is sized for three characters.">
              MAX
            </Trans>
          </InlineButton>
        }
      />
    </>
  );
}
